import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { IMAGES, CONTACT } from "../data/site";

const LINKS = [
  { to: "/", label: "Home", id: "home" },
  { to: "/team", label: "Team", id: "team" },
  { to: "/groups", label: "Groups", id: "groups" },
  { to: "/retreats", label: "Retreats", id: "retreats" },
  { to: "/blog", label: "Journal", id: "blog" },
  { to: "/resources", label: "Shop", id: "resources" },
  { to: "/contact", label: "Contact", id: "contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-gold/20" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between h-20">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
          <img src={IMAGES.logo} alt="Beyond Blessed" className="h-11 w-auto" />
          <span className="hidden sm:block font-serif text-lg leading-tight text-navy">
            Beyond Blessed<span className="block text-[10px] tracking-[0.25em] uppercase text-teal font-sans">Consulting & Counseling</span>
          </span>
        </Link>

        <div className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <Link
              key={l.id}
              to={l.to}
              data-testid={`nav-${l.id}-link`}
              className={`link-underline text-sm tracking-wide font-medium ${
                pathname === l.to ? "text-teal" : "text-navy/70 hover:text-navy"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href={CONTACT.growTherapy}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-grow-therapy"
            className="text-xs font-medium text-teal hover:text-navy link-underline"
          >
            Grow Therapy
          </a>
          <a
            href={CONTACT.portal}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-client-portal"
            className="text-sm font-medium text-navy/70 hover:text-navy link-underline"
          >
            Client Portal
          </a>
          <a
            href={CONTACT.request}
            target="_blank"
            rel="noreferrer"
            data-testid="nav-request-appointment"
            className="text-sm font-medium bg-navy text-cream px-5 py-2.5 rounded-full hover:bg-teal transition-colors duration-300"
          >
            Request Appointment
          </a>
        </div>

        <button
          className="lg:hidden text-navy"
          onClick={() => setOpen(!open)}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden bg-cream border-t border-gold/20 px-6 py-6 flex flex-col gap-4">
          {LINKS.map((l) => (
            <Link key={l.id} to={l.to} data-testid={`mobile-nav-${l.id}-link`} className="text-navy text-lg font-serif">
              {l.label}
            </Link>
          ))}
          <a href={CONTACT.request} target="_blank" rel="noreferrer" className="mt-2 text-center bg-navy text-cream px-5 py-3 rounded-full" data-testid="mobile-request-appointment">
            Request Appointment
          </a>
        </div>
      )}
    </header>
  );
};
